<template>
  <div class="flex flex-col gap-2.5">
    <h2 class="text-sm font-semibold">Create Programmes</h2>
    <div class="flex flex-col bg-primary/10 p-1.5 rounded">
      <div class="flex items-start gap-1.5">
        <i class="pi pi-info-circle text-primary mt-1 text-xs"></i>
        <span class="text-xs">
          Select a department, fill in programme details, and click 'Add Programme'.
          You can add multiple programmes, then click 'Submit All' to save.
        </span>
      </div>
    </div>

    <div class="flex flex-col gap-2.5">
      <div class="flex flex-col gap-1.5">
        <label class="font-medium text-xs">Select Faculty</label>
        <Select
          v-model="selectedFacultyId"
          :options="faculties"
          optionLabel="faculty_name"
          optionValue="id"
          placeholder="Select a faculty"
          class="w-full"
          data-testid="programme-modal-faculty-select"
        />
      </div>

      <div class="flex flex-col gap-1.5">
        <label class="font-medium text-xs">Select Department</label>
        <Select
          v-model="departmentId"
          :options="departmentsList"
          optionLabel="department_name"
          optionValue="id"
          placeholder="Select a department"
          class="w-full"
          :disabled="!selectedFacultyId"
          data-testid="programme-modal-department-select"
        />
      </div>
    </div>

    <form @submit.prevent="addProgramme" class="flex flex-col gap-2.5">
      <div class="grid grid-cols-1 md:grid-cols-2 gap-2">
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Programme Name</label>
          <InputText
            v-model="program_name"
            placeholder="Programme Name"
            class="w-full"
            :class="{ 'p-invalid': getErrorMessage('program_name') }"
            data-testid="programme-modal-name-input"
          />
          <small v-if="getErrorMessage('program_name')" class="text-red-500">
            {{ getErrorMessage('program_name') }}
          </small>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Programme Code</label>
          <InputText
            v-model="prog_code"
            placeholder="Code"
            class="w-full"
            :class="{ 'p-invalid': getErrorMessage('prog_code') }"
            data-testid="programme-modal-code-input"
          />
          <small v-if="getErrorMessage('prog_code')" class="text-red-500">
            {{ getErrorMessage('prog_code') }}
          </small>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Type</label>
          <Select
            v-model="type"
            :options="typeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Type"
            class="w-full"
            data-testid="programme-modal-type-select"
            @change="handleTypeChange"
          />
          <div v-if="showAddTypeInput" class="flex gap-1.5 mt-1.5 items-center">
            <InputText
              v-model="newType"
              placeholder="Enter new type"
              class="flex-1"
              data-testid="programme-modal-new-type-input"
            />
            <Button icon="pi pi-check" class="p-button-sm" data-testid="programme-modal-confirm-type-btn" @click="addNewType" />
            <Button icon="pi pi-times" class="p-button-sm p-button-text p-button-secondary" data-testid="programme-modal-cancel-type-btn" @click="showAddTypeInput = false" />
          </div>
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Duration in semester</label>
          <InputNumber
            v-model="duration"
            placeholder="Duration in semester"
            :min="1"
            class="w-full"
            inputClass="w-full"
            data-testid="programme-modal-duration-input"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Degree</label>
          <Select
            v-model="degree"
            :options="degreeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Degree"
            class="w-full"
            data-testid="programme-modal-degree-select"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Calculation Method</label>
          <Select
            v-model="calculation_method"
            :options="calculationMethodOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Select Method"
            class="w-full"
            showClear
            data-testid="programme-modal-calc-method-select"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Min Units</label>
          <InputNumber
            v-model="min_units"
            placeholder="Min Units"
            :min="0"
            class="w-full"
            inputClass="w-full"
            data-testid="programme-modal-min-units-input"
          />
        </div>
        <div class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Delivery Mode</label>
          <Select
            v-model="delivery_mode"
            :options="deliveryModeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Regular (default)"
            class="w-full"
            showClear
            data-testid="programme-modal-delivery-mode-select"
            @change="handleDeliveryModeChange"
          />
          <small class="text-xs text-gray-500">
            Leave on Regular unless this programme teaches in sequential time-boxed blocks;
            Modular/Residence gate registration behind modules defined in Settings → Tertiary → Modules.
          </small>
        </div>
        <div v-if="usesModules" class="flex flex-col gap-1.5">
          <label class="text-xs font-medium">Module Results</label>
          <Select
            v-model="module_result_mode"
            :options="moduleResultModeOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Roll up into semester (default)"
            class="w-full"
            showClear
            data-testid="programme-modal-result-mode-select"
          />
        </div>
      </div>

      <div class="flex justify-end">
        <Button
          type="submit"
          label="Add Programme"
          icon="pi pi-plus"
          size="small"
          class="bg-primary"
          :disabled="!canAddProgramme"
          data-testid="programme-modal-add-btn"
        />
      </div>
    </form>

    <ul v-if="programmes.length" class="flex flex-col gap-1.5 mb-1.5">
      <li v-for="(prog, idx) in programmes" :key="idx"
        class="flex items-center justify-between bg-gray-100 px-2.5 py-1.5 rounded">
        <span class="text-xs">
          {{ prog.program_name }} ({{ prog.prog_code }}) - {{ prog.type }}, {{ prog.duration }} sem, {{ prog.degree }},
          {{ prog.min_units }} units<span v-if="prog.calculation_method">, {{ prog.calculation_method }}</span><span v-if="prog.delivery_mode && prog.delivery_mode !== ProgrammeDeliveryModeEnum.Regular">, {{ prog.delivery_mode.toLowerCase() }}</span>
        </span>
        <Button icon="pi pi-trash" class="p-button-text p-button-danger p-button-sm" :data-testid="`programme-modal-remove-${idx}`" @click="removeProgramme(idx)" />
      </li>
    </ul>

    <div class="flex justify-end mt-1.5">
      <Button
        :label="isSubmitting ? 'Submitting...' : 'Submit All'"
        size="small"
        :loading="isSubmitting"
        :disabled="programmes.length === 0"
        class="!bg-primary !border-primary"
        data-testid="programme-modal-submit-btn"
        @click="submitAll"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { useTertiaryMutations } from '@/composables/mutations/tertiary/useTertiaryMutations';
import { useFaculties } from '@/composables/queries/useFaculties';
import { useFormErrors } from '@/composables/useFormErrors';
import type { ProgrammeInput, ModuleResultModeEnum } from '@/types/composition-functions';
import { ProgrammeDeliveryModeEnum } from '@/types/composition-functions';
import {
  DEGREE_OPTIONS,
  CALCULATION_METHOD_OPTIONS,
  DELIVERY_MODE_OPTIONS,
  MODULE_RESULT_MODE_OPTIONS,
} from '@/constants/programme-options';

const emit = defineEmits<{ success: [] }>();

const { createTertiaryProgramme } = useTertiaryMutations();
const { faculties } = useFaculties();
const { handleServerErrors, clearErrors, getErrorMessage } = useFormErrors();

const selectedFacultyId = ref('');
const departmentId = ref('');
const program_name = ref('');
const prog_code = ref('');
const type = ref('');
const duration = ref<number | null>(null);
const min_units = ref<number | null>(null);
const degree = ref('');
const calculation_method = ref<string | null>(null);
const delivery_mode = ref<ProgrammeDeliveryModeEnum | null>(null);
const module_result_mode = ref<ModuleResultModeEnum | null>(null);
const programmes = ref<ProgrammeInput[]>([]);
const isSubmitting = ref(false);

const departmentsList = computed(() => {
  const faculty = faculties.value.find(f => f.id === selectedFacultyId.value);
  return faculty?.departments ?? [];
});

const degreeOptions = DEGREE_OPTIONS;
const calculationMethodOptions = CALCULATION_METHOD_OPTIONS;
const deliveryModeOptions = DELIVERY_MODE_OPTIONS;
const moduleResultModeOptions = MODULE_RESULT_MODE_OPTIONS;

const usesModules = computed(() =>
  delivery_mode.value === ProgrammeDeliveryModeEnum.Modular ||
  delivery_mode.value === ProgrammeDeliveryModeEnum.Residence,
);

function handleDeliveryModeChange() {
  if (!usesModules.value) module_result_mode.value = null;
}

const typeOptions = ref([
  { label: 'Full time (FT)', value: 'Full time (FT)' },
  { label: 'Part time (PT)', value: 'Part time (PT)' },
  { label: 'Distance Learning (DL)', value: 'Distance Learning (DL)' },
  { label: 'Add more type...', value: '__add_more__' },
]);

const showAddTypeInput = ref(false);
const newType = ref('');

const canAddProgramme = computed(() =>
  !!departmentId.value &&
  !!program_name.value.trim() &&
  !!prog_code.value.trim() &&
  !!type.value.trim() &&
  duration.value !== null &&
  min_units.value !== null &&
  !!degree.value.trim(),
);

function addProgramme() {
  if (!canAddProgramme.value) return;

  programmes.value.push({
    department_id: departmentId.value,
    program_name: program_name.value.trim(),
    prog_code: prog_code.value.trim(),
    type: type.value.trim(),
    duration: duration.value!,
    degree: degree.value.trim(),
    min_units: min_units.value!,
    calculation_method: calculation_method.value || undefined,
    delivery_mode: delivery_mode.value || undefined,
    module_result_mode: (usesModules.value && module_result_mode.value) || undefined,
  });

  program_name.value = '';
  prog_code.value = '';
  type.value = '';
  duration.value = null;
  degree.value = '';
  min_units.value = null;
  calculation_method.value = null;
  delivery_mode.value = null;
  module_result_mode.value = null;
}

function handleTypeChange() {
  if (type.value === '__add_more__') {
    showAddTypeInput.value = true;
    type.value = '';
  } else {
    showAddTypeInput.value = false;
  }
}

function addNewType() {
  if (newType.value.trim()) {
    typeOptions.value.splice(typeOptions.value.length - 1, 0, {
      label: newType.value.trim(),
      value: newType.value.trim(),
    });
    type.value = newType.value.trim();
    newType.value = '';
    showAddTypeInput.value = false;
  }
}

function removeProgramme(idx: number) {
  programmes.value.splice(idx, 1);
}

async function submitAll() {
  if (!programmes.value.length) return;
  isSubmitting.value = true;
  clearErrors();
  const total = programmes.value.length;
  let failed = 0;
  let lastError: unknown = null;

  for (const prog of programmes.value) {
    try {
      await createTertiaryProgramme(prog);
    } catch (err) {
      failed++;
      lastError = err;
    }
  }

  isSubmitting.value = false;

  if (failed === total && lastError) {
    handleServerErrors(lastError);
  } else {
    programmes.value = [];
    emit('success');
  }
}
</script>
